import { createFormHookContexts, useStore, type AnyFieldApi } from '@tanstack/react-form';

/**
 * `Object.is`, because shallow equality fails on a `File` (zero own keys); an explicit subscription for
 * the React Compiler. The `any` casts: Derived/ReadonlyStore miss `useStore`'s AnyAtom across versions.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFieldValue<T>(field: { store: any; state: { value: T } }): T {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return useStore(field.store, (state: any) => state.value, Object.is as any);
}

export function hasFieldError(meta: AnyFieldApi['state']['meta']): boolean {
	if (meta.isValid) return false;
	const hasSubmitErrors = (meta.errorMap as Record<string, unknown[]> | undefined)?.onSubmit?.length;
	return meta.isTouched || !!hasSubmitErrors;
}

/** For a field that renders its own message slot, like `AmountInput`, to point `aria-describedby` at it. */
export function fieldErrorMessage(fieldState: AnyFieldApi['state']): string | undefined {
	const { meta } = fieldState;
	if (!hasFieldError(meta)) return undefined;
	return meta.errors.map((error: Error) => error.message).join(', ') || undefined;
}

// TODO: `field.state`, not `field`, for tanstack form under the React Compiler: https://github.com/TanStack/form/issues/1379
export function FieldInfo(props: { fieldState: AnyFieldApi['state'] }) {
	const message = fieldErrorMessage(props.fieldState);
	if (message == null) return null;
	return (
		<p data-slot="form-message" className="text-destructive m-1 text-sm">
			{message}
		</p>
	);
}

export const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasRequiredConstraint(schema: any): boolean {
	const def = schema._zod.def;
	if (def.type === 'enum') return true;
	if (def.type === 'literal') return true;
	if (def.type === 'union') return true;
	// z.email(), z.url() and the like cannot be empty.
	if (def.check === 'string_format') return true;

	const checks: { _zod: { def: Record<string, unknown> } }[] | undefined = def.checks;
	if (!checks?.length) return false;

	return checks.some((c) => {
		const d = c._zod.def;
		if (d.check === 'min_length') return (d.minimum as number) >= 1;
		if (d.check === 'greater_than') return d.inclusive ? (d.value as number) >= 1 : (d.value as number) > 0;
		return false;
	});
}

/** Through Zod v4's internal `_zod` API; `undefined` when the path cannot be resolved. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isOptionalInSchema(schema: any, fieldPath: string): boolean | undefined {
	const parts = fieldPath.replace(/\[(\d+)\]/g, '.$1').split('.');
	let current = schema;

	for (const part of parts) {
		if (!current?._zod) return undefined;
		const defType = current._zod.def.type;

		if (defType === 'object' && current._zod.def.shape) {
			current = current._zod.def.shape[part];
		} else if (defType === 'array' && /^\d+$/.test(part)) {
			current = current._zod.def.element;
		} else if (defType === 'record') {
			// A checked record (translatableText requiring content) makes its values required.
			if (current._zod.def.checks?.length) return false;
			current = current._zod.def.valueType;
		} else {
			return undefined;
		}
	}

	if (!current?._zod) return undefined;
	if (current._zod.optin === 'optional') return true;
	return !hasRequiredConstraint(current);
}

export function useIsFieldOptional(fieldName: string, explicitOptional?: boolean): boolean {
	const field = useFieldContext();
	if (explicitOptional !== undefined) return explicitOptional;

	const validators = field.form.options.validators;
	const formSchema = validators?.onSubmit ?? validators?.onChange ?? validators?.onBlur;
	if (formSchema) {
		const result = isOptionalInSchema(formSchema, fieldName);
		if (result !== undefined) return result;
	}

	return !(
		field.options.validators?.onSubmit ??
		field.options.validators?.onChange ??
		field.options.validators?.onBlur
	);
}
